import React, { useState } from 'react';
import { createWithdrawal } from '../services/api';
import './WithdrawModal.css';

function WithdrawModal({ onClose, onSuccess, currentBalance }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const WITHDRAWAL_MIN = 20;
  const maxWithdrawal = currentBalance || 0;

  const quickAmounts = [20, 50, 100, 250, 500].filter(amt => amt <= maxWithdrawal);

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
    setError(null);
  };

  const handleWithdraw = async () => {
    const withdrawAmount = parseFloat(amount);

    if (!withdrawAmount || withdrawAmount < WITHDRAWAL_MIN) {
      setError(`Minimum withdrawal is $${WITHDRAWAL_MIN}`);
      return;
    }

    if (withdrawAmount > maxWithdrawal) {
      setError(`Insufficient balance. Maximum withdrawal: $${maxWithdrawal.toFixed(2)}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createWithdrawal(withdrawAmount);
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(response.message || 'Failed to process withdrawal');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to process withdrawal');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content withdraw-modal" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>×</button>
          <div className="withdraw-success">
            <div className="success-icon">✓</div>
            <h2>Withdrawal Initiated</h2>
            <p>
              Your withdrawal of ${parseFloat(amount).toFixed(2)} is being processed.
              Funds will be transferred to your account in 1-3 business days.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content withdraw-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>

        <h2>Withdraw Funds</h2>
        <p className="withdraw-subtitle">
          Current Balance: <span className="balance-highlight">${maxWithdrawal.toFixed(2)}</span>
        </p>

        <div className="quick-amounts">
          {quickAmounts.length > 0 ? (
            quickAmounts.map((value) => (
              <button
                key={value}
                type="button"
                className={`quick-amount-btn ${amount === value.toString() ? 'active' : ''}`}
                onClick={() => handleQuickAmount(value)}
                disabled={loading}
              >
                ${value}
              </button>
            ))
          ) : (
            <p className="no-quick-amounts">
              Minimum withdrawal is ${WITHDRAWAL_MIN}. Please add more funds to your account.
            </p>
          )}
        </div>

        <div className="custom-amount">
          <label htmlFor="withdraw-amount">Withdrawal Amount</label>
          <div className="amount-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              id="withdraw-amount"
              type="number"
              min={WITHDRAWAL_MIN}
              max={maxWithdrawal}
              step="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError(null);
              }}
              placeholder={`${WITHDRAWAL_MIN} - ${maxWithdrawal.toFixed(2)}`}
              className="amount-input"
              disabled={loading}
            />
          </div>
          <small className="amount-hint">
            Min: ${WITHDRAWAL_MIN} | Max: ${maxWithdrawal.toFixed(2)} (your balance)
          </small>
        </div>

        <div className="withdraw-notice">
          <div className="notice-icon">ℹ️</div>
          <div className="notice-content">
            <strong>Processing Time:</strong> Withdrawals typically take 1-3 business days to appear in your account.
          </div>
        </div>

        {error && (
          <div className="withdraw-error">
            {error}
          </div>
        )}

        <div className="withdraw-actions">
          <button
            type="button"
            className="withdraw-cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="withdraw-submit-btn"
            onClick={handleWithdraw}
            disabled={!amount || loading || maxWithdrawal < WITHDRAWAL_MIN}
          >
            {loading ? 'Processing...' : `Withdraw ${amount ? `$${parseFloat(amount).toFixed(2)}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WithdrawModal;
